
import { Card, CardContent } from "@/components/ui/card";
import { Profile } from "@/types/database.types";
import { UserHeader } from "./UserHeader";
import { EditableUserTypeSelector } from "./EditableUserTypeSelector";
import { AdminToggle } from "./AdminToggle";
import { PEOAssignmentSelector } from "./PEOAssignmentSelector";
import { UserApplicationsButton } from "./UserApplicationsButton";
import { UserApplicationsSheet } from "./UserApplicationsSheet";
import { isSuperAdmin } from "@/services/admin.service";
import { useFetchApplications } from "@/hooks/application/use-fetch-applications";
import { useState, useEffect } from "react";

interface EnhancedUserCardProps {
  user: Profile;
  isAdmin: boolean;
  formatDate: (date: string) => string;
  getInitials: (firstName?: string | null, lastName?: string | null) => string;
  onAdminStatusChange: () => void;
  onUserClick: (user: Profile) => void;
}

export const EnhancedUserCard = ({ 
  user, 
  isAdmin,
  formatDate, 
  getInitials,
  onAdminStatusChange,
  onUserClick
}: EnhancedUserCardProps) => {
  const [isUserSuperAdmin, setIsUserSuperAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [applicationsSheetOpen, setApplicationsSheetOpen] = useState(false);
  
  const { fetchApplications, isLoading: isApplicationsLoading } = useFetchApplications();
  const [userApplications, setUserApplications] = useState<any[]>([]);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user?.id) {
        try {
          const isAdmin = await isSuperAdmin(user.id);
          setIsUserSuperAdmin(isAdmin);
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsUserSuperAdmin(false);
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    checkAdminStatus();
  }, [user]);

  const handleInteractiveClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleApplicationsClick = async (event?: React.MouseEvent) => {
    if (event) event.stopPropagation();
    try {
      const applications = await fetchApplications(user.id);
      setUserApplications(applications);
      setApplicationsSheetOpen(true);
    } catch (error) {
      console.error('Error fetching user applications:', error);
      setUserApplications([]);
      setApplicationsSheetOpen(true);
    }
  };

  const handleApplicationsButtonClick = () => {
    handleApplicationsClick();
  };

  return (
    <Card 
      className="glass-card overflow-hidden border-primary/10 transition-all duration-300 hover:shadow-md cursor-pointer hover:border-primary/20"
      onClick={() => onUserClick(user)}
    >
      <UserHeader 
        user={user}
        isAdmin={isAdmin}
        getInitials={getInitials}
      />
      
      <CardContent className="p-4 space-y-4">
        <div className="flex justify-between">
          <span className="text-sm text-muted-foreground">Joined</span>
          <span className="text-sm">{formatDate(user.created_at || '')}</span>
        </div>
        
        <div onClick={handleInteractiveClick}>
          <EditableUserTypeSelector 
            user={user}
            isProtectedEmail={isUserSuperAdmin}
            onUserTypeChange={onAdminStatusChange}
          />
        </div>
        
        <div onClick={handleInteractiveClick}>
          <PEOAssignmentSelector
            user={user}
            onAssignmentChange={onAdminStatusChange}
          />
        </div>
        
        <div onClick={handleInteractiveClick}>
          <UserApplicationsButton
            applicationCount={user.application_count ?? 0}
            isLoading={isApplicationsLoading}
            onClick={handleApplicationsButtonClick}
          />
        </div>
        
        <div onClick={handleInteractiveClick}>
          <AdminToggle 
            user={user}
            isAdmin={isAdmin}
            isProtectedEmail={isUserSuperAdmin}
            onAdminStatusChange={onAdminStatusChange}
          />
        </div>
      </CardContent>
      
      <UserApplicationsSheet
        user={user}
        applications={userApplications}
        isLoading={isApplicationsLoading}
        isOpen={applicationsSheetOpen}
        onOpenChange={setApplicationsSheetOpen}
        onApplicationUpdated={() => {
          // Refetch applications when one is updated
          handleApplicationsClick();
        }}
      />
    </Card>
  );
};
